import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";

import { useAsyncEffect } from "@app/hooks/use-async-effect";

import {
  CartRecommendationsResponse,
  SearchspringAutocomplete,
  SearchspringCategory,
  SearchspringCollection,
  SearchspringFetcher,
  SearchspringProduct,
  SearchspringRecommend,
  SearchspringSearch,
  SearchspringSearchResponse,
  SearchspringTrending,
  SearchspringTrendingResponse,
} from "@app/lib/searchspring";
import { useForceUpdate } from "@app/hooks/use-force-update";
import { CART_EVENTS } from "@app/lib/events";

type UseSearchspringInput<Client extends SearchspringFetcher<Response>, Response> = {
  client: Client;
  options?: Options;
  isInitiallyRun?: boolean;
  shouldSkipEmptyOptions?: boolean;
};

export function useSearchspring<Client extends SearchspringFetcher<Response>, Response>({
  client,
  options: _options = new Map(),
  isInitiallyRun = true,
  shouldSkipEmptyOptions = false,
}: UseSearchspringInput<Client, Response>) {
  // Request State
  const [response, setResponse] = useState<Response>();
  const [updateVar, forceUpdate] = useForceUpdate();

  // Object for managing filtering
  const params = useRef(client.params);
  const [options, setOptions] = useState(new Map(_options));

  // A function for correctly updating the filtering array for Searchspring
  const refine = useCallback(
    (key: string, value: OptionValue) => {
      if (value === options.get(key)) return;
      setOptions((prev) => {
        const next = new Map(prev);
        if (!value) next.delete(key);
        else next.set(key, value);
        return next;
      });
    },
    [setOptions],
  );

  const [isLoading, error] = useAsyncEffect(
    async (isInitialRun) => {
      if (!isInitiallyRun && isInitialRun) return;
      if (shouldSkipEmptyOptions && options.size === 0) return;
      console.log("Fetching Searchspring", client, options, isInitiallyRun, isInitialRun);
      const params = Object.fromEntries(options.entries());
      client.updateParams(params);
      const response = await client.fetch();
      setResponse(response);
    },

    async () => {
      client.controller.abort("Outdated Searchspring payload, aborting current request");
      client.refreshAbortController();
    },
    [options, updateVar],
  );

  useEffect(() => {
    document.addEventListener(CART_EVENTS.FETCHED, (e) => {
      forceUpdate();
    });
  }, []);

  return {
    isLoading,
    response,
    error,
    options,
    params,
    setOptions,
    refine,
  };
}

const SearchspringContext = createContext({} as ReturnType<typeof useSearchspring>);

export const useSearchspringContext = <Client extends SearchspringFetcher<Response>, Response>() =>
  useContext(SearchspringContext) as ReturnType<typeof useSearchspring<Client, Response>>;

export const useSearchspringSearchContext = () =>
  useSearchspringContext<SearchspringFetcher<SearchspringSearchResponse>, SearchspringSearchResponse>();

export const useSearchspringTrendingContext = () =>
  useSearchspringContext<SearchspringFetcher<SearchspringTrendingResponse>, SearchspringTrendingResponse>();

export const useSearchspringRecommendContext = () =>
  useSearchspringContext<SearchspringFetcher<CartRecommendationsResponse[]>, CartRecommendationsResponse[]>();

export type OptionValue = string | Set<string> | number;
export type Options = Map<string, OptionValue>;

type ProviderCoreProps<Client extends SearchspringFetcher<Response>, Response> = {
  client: Client;
  children: ReactNode;
  options: Options;
  isInitiallyRun?: boolean;
};

export function SearchspringProvider<Client extends SearchspringFetcher<Response>, Response>({
  client,
  options,
  children,
  isInitiallyRun = true,
}: ProviderCoreProps<Client, Response>) {
  const value = useSearchspring({ client, options, isInitiallyRun });
  return <SearchspringContext.Provider value={value}>{children}</SearchspringContext.Provider>;
}

type ProviderGeneratorProps = {
  children: ReactNode;
  category: SearchspringCategory;
  isInitiallyRun?: boolean;
  [option: string]: any;
};

export function generateSearchspringProvider<Client extends typeof SearchspringFetcher<Response>, Response>(
  ClientClass: Client,
) {
  return function ({ children, category, isInitiallyRun, ...options }: ProviderGeneratorProps) {
    const client = useRef(new ClientClass(category));
    return (
      <SearchspringProvider
        client={client.current}
        options={new Map(Object.entries(options))}
        isInitiallyRun={isInitiallyRun}
      >
        {children}
      </SearchspringProvider>
    );
  };
}

// All definitions for usable Searchspring providers are below:

export const SearchspringAutocompleteProvider = generateSearchspringProvider(SearchspringAutocomplete);
export const SearchspringProductsProvider = generateSearchspringProvider(SearchspringProduct);
export const SearchspringCollectionProvider = generateSearchspringProvider(SearchspringCollection);
export const SearchspringRecommendProvider = generateSearchspringProvider(SearchspringRecommend);
export const SearchspringTrendingProvider = generateSearchspringProvider(SearchspringTrending);
export const SearchspringSearchProvider = generateSearchspringProvider(SearchspringSearch);
